import React, { useState, useEffect } from 'react';
const useId = () => React.useMemo(() => "id_" + Math.random().toString(36).substr(2, 9), []);
import styles from './ClubValueGauge.module.css';

const SEGMENT_COUNT = 56;

const parseCurrencyValue = (rawValue) => {
    if (typeof rawValue === 'number') return rawValue;
    const cleaned = String(rawValue || '').replace(/[£$,\sM]/gi, '');
    return parseFloat(cleaned) || 0;
};

export default function ClubValueGauge({ value, isMember = true }) {
    const [animatedRotation, setAnimatedRotation] = useState(-90);
    const [isAnimating, setIsAnimating] = useState(false);
    const idSafe = useId().replace(/[^a-zA-Z0-9_-]/g, '');

    const numericValue = parseCurrencyValue(value.numericValue || value.current);
    const forcedValue = value.forcedValue || value.floor;
    const premiumValue = value.premiumValue || value.ceiling;

    const fVal = (forcedValue && parseFloat(parseCurrencyValue(forcedValue)) > 0)
        ? parseCurrencyValue(forcedValue)
        : (numericValue * 0.8);
    const pVal = (premiumValue && parseFloat(parseCurrencyValue(premiumValue)) > 0)
        ? parseCurrencyValue(premiumValue)
        : (numericValue * 1.25);

    const formatCurrency = (val, decimals = 0) => {
        const num = parseFloat(val) || 0;
        const millions = num > 100000 ? num / 1000000 : num;
        return millions.toFixed(decimals);
    };

    const mainDisplay = `${formatCurrency(numericValue, 1)}M`;
    const spread = pVal - fVal;
    const position = (spread > 0 && numericValue > 0) ? (numericValue - fVal) / spread : 0;
    const percentage = (numericValue > 0) ? Math.max(0, Math.min(1, position)) : 0;
    const targetRotation = (isMember && percentage > 0) ? (-90 + (percentage * 180)) : -90;

    useEffect(() => {
        if (isMember && percentage > 0) {
            setIsAnimating(false);
            setAnimatedRotation(-90);

            const timer = setTimeout(() => {
                setIsAnimating(true);
                setAnimatedRotation(targetRotation);
            }, 100);
            return () => clearTimeout(timer);
        }

        setAnimatedRotation(-90);
        setIsAnimating(false);
        return undefined;
    }, [targetRotation, isMember, percentage]);

    const activeSegmentCount = isMember ? Math.max(0, Math.ceil(percentage * SEGMENT_COUNT)) : 0;

    const interpolateHex = (startHex, endHex, ratio) => {
        const start = startHex.match(/\w\w/g).map((value) => parseInt(value, 16));
        const end = endHex.match(/\w\w/g).map((value) => parseInt(value, 16));
        const channels = start.map((value, index) => {
            const mixed = Math.round(value + ((end[index] - value) * ratio));
            return mixed.toString(16).padStart(2, '0');
        });

        return `#${channels.join('')}`;
    };

    const getSegmentFadeRatio = (index) => (
        activeSegmentCount <= 1
            ? 0
            : Math.min(1, index / (activeSegmentCount - 1))
    );

    const getActiveSegmentColor = (index) => {
        const fadeRatio = getSegmentFadeRatio(index);

        return interpolateHex('6A4308', 'F0B34E', fadeRatio);
    };

    const renderBlurDefs = (suffix) => (
        <defs>
            <filter id={`activeSegmentsBlur-${suffix}-${idSafe}`} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="6.35" />
            </filter>
        </defs>
    );

    const renderSegments = ({ isMobile = false, activeOnly = false }) => (
        [...Array(SEGMENT_COUNT)].map((_, i) => {
            const angle = -90 + (i * 180) / (SEGMENT_COUNT - 1);
            const isActive = i < activeSegmentCount;

            if (isActive !== activeOnly) return null;

            return (
                <rect
                    key={`${activeOnly ? 'active' : 'inactive'}-${i}`}
                    x={isMobile ? '169.5' : '98'}
                    y={isMobile ? '18' : '20'}
                    width={isMobile ? '4.2' : '2.4'}
                    height={isMobile ? '31' : '18'}
                    fill={isActive ? getActiveSegmentColor(i) : (isMobile ? '#2a2a2a' : '#333')}
                    style={{
                        transform: `rotate(${angle}deg)`,
                        transformOrigin: isMobile ? '173px 150px' : '100px 100px',
                        opacity: isActive ? 1 : (isMobile ? 0.32 : 0.4),
                        transition: isMobile
                            ? `fill 0.5s ease ${i * 0.02}s, opacity 0.5s ease ${i * 0.02}s`
                            : `all 0.5s ease ${i * 0.02}s`
                    }}
                />
            );
        })
    );

    return (
        <section className={styles.valueCard} aria-label="Forensic value">
            <div className={`${styles.gaugeSvgWrapper} ${styles.gaugeDesktop}`}>
                <svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
                    {renderBlurDefs('desktop')}
                    {renderSegments({ activeOnly: false })}
                    <g filter={`url(#activeSegmentsBlur-desktop-${idSafe})`} opacity="0.75">
                        {renderSegments({ activeOnly: true })}
                    </g>
                    {renderSegments({ activeOnly: true })}

                    <g transform="translate(100, 95)">
                        <text fill="#f2aa30" fontSize="26" fontWeight="900" textAnchor="middle" style={{ fontFamily: '"Barlow Condensed", sans-serif' }}>
                            £{mainDisplay}
                        </text>
                        <text y="12" fill="#f2aa30" fontSize="6" fontWeight="900" textAnchor="middle" style={{ textTransform: 'uppercase', fontFamily: '"Barlow Condensed", sans-serif' }}>
                            SQUAD VALUE
                        </text>
                    </g>

                    {/* Needle moved to end to stay on top of everything */}
                    <g
                        transform={`rotate(${animatedRotation}, 100, 100)`}
                        style={{ transition: isAnimating ? 'transform 5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none' }}
                    >
                        <path d="M 99 100 L 100 30 L 101 100 Z" fill={isMember ? '#ffffff' : '#333'} />
                        <circle cx="100" cy="100" r="3" fill={isMember ? 'var(--gold)' : '#222'} />
                    </g>
                </svg>
            </div>

            <div className={`${styles.gaugeSvgWrapper} ${styles.gaugeMobile}`}>
                <svg viewBox="0 0 346 172" className={styles.forensicGaugeSvg}>
                    {renderBlurDefs('mobile')}
                    {renderSegments({ isMobile: true, activeOnly: false })}
                    <g filter={`url(#activeSegmentsBlur-mobile-${idSafe})`} opacity="0.75">
                        {renderSegments({ isMobile: true, activeOnly: true })}
                    </g>
                    {renderSegments({ isMobile: true, activeOnly: true })}

                    <g transform="translate(173, 135)">
                        <text fill="#f2aa30" fontSize="36" fontWeight="900" textAnchor="middle" style={{ fontFamily: '"Barlow Condensed", sans-serif' }}>
                            £{mainDisplay}
                        </text>
                        <text y="22" fill="#f2aa30" fontSize="14" fontWeight="900" textAnchor="middle" style={{ textTransform: 'uppercase', fontFamily: '"Barlow Condensed", sans-serif' }}>
                            SQUAD VALUE
                        </text>
                    </g>

                    {/* Needle moved to end to stay on top of everything */}
                    <g
                        transform={`rotate(${animatedRotation}, 173, 150)`}
                        style={{ transition: isAnimating ? 'transform 5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none' }}
                    >
                        <path d="M 171.5 150 L 173 64 L 174.5 150 Z" fill={isMember ? '#ffffff' : '#333'} />
                        <circle cx="173" cy="150" r="5" fill={isMember ? 'var(--gold)' : '#222'} />
                    </g>
                </svg>
            </div>
        </section>
    );
}
